import { useRef } from "react";
import { t } from "i18next";
import ReactStars from "react-rating-stars-component";
import { DocumentUpload } from "iconsax-react";
import { ReactComponent as StarIcon } from "../../feedback/assets/icons/starIcon.svg";
import { ReactComponent as EmptyStarIcon } from "../../feedback/assets/icons/emptyStarIcon.svg";

/**
 * Shared respondent-facing renderer for a survey definition
 * ({criteria: [{id, name, criteria_state_type, states, ...}]}).
 * replies shape: { [criteriaInfoId]: { state, description, file } }
 */
export default function SurveyFormRenderer({
    criteria,
    replies,
    onChange,
    readOnly = false,
    onlyCriterionId = null,
}) {
    const visibleCriteria = onlyCriterionId
        ? criteria.filter((criterion) => criterion.id === onlyCriterionId)
        : criteria;

    return (
        <div className="survey-form-renderer">
            {visibleCriteria.map((criterion, index) => (
                <CriterionField
                    key={criterion.id ?? index}
                    index={
                        onlyCriterionId
                            ? criteria.findIndex(
                                  (item) => item.id === criterion.id
                              )
                            : index
                    }
                    criterion={criterion}
                    reply={replies[criterion.id] || {}}
                    onChange={(patch) =>
                        onChange?.(criterion.id, patch)
                    }
                    readOnly={readOnly}
                />
            ))}
        </div>
    );
}

function CriterionField({ criterion, index, reply, onChange, readOnly }) {
    const fileInputRef = useRef(null);
    const sortedStates = [...(criterion.states || [])].sort(
        (a, b) => a.value - b.value
    );
    const groupLabel = criterion.name || `criterion-${index + 1}`;

    const selectState = (state) => {
        if (readOnly) return;
        onChange({ ...reply, state });
    };

    return (
        <fieldset className="survey-criterion">
            <legend>
                <span className="survey-criterion-index" aria-hidden="true">
                    {index + 1}
                </span>
                <span className="survey-criterion-title">{criterion.name}</span>
            </legend>

            {criterion.criteria_state_type === 1 && (
                <div
                    className="survey-numeral-options"
                    role="radiogroup"
                    aria-label={groupLabel}
                >
                    {sortedStates.map((state) => {
                        const selected =
                            reply.state?.value === state.value ||
                            reply.state === state.value;
                        return (
                            <button
                                type="button"
                                key={state.id ?? state.value}
                                disabled={readOnly}
                                className={selected ? "selected" : ""}
                                onClick={() => selectState(state)}
                                aria-pressed={selected}
                                aria-label={state.name}
                            >
                                {state.name}
                            </button>
                        );
                    })}
                </div>
            )}

            {criterion.criteria_state_type === 2 && (
                <div
                    className="survey-text-options"
                    role="radiogroup"
                    aria-label={groupLabel}
                >
                    {sortedStates.map((state) => {
                        const selected =
                            reply.state?.value === state.value ||
                            reply.state === state.value;
                        return (
                            <label
                                key={state.id ?? state.value}
                                className={selected ? "selected" : ""}
                            >
                                <input
                                    type="radio"
                                    name={`survey-criterion-${criterion.id}`}
                                    disabled={readOnly}
                                    checked={selected}
                                    onChange={() => selectState(state)}
                                />
                                <span>{state.name}</span>
                            </label>
                        );
                    })}
                </div>
            )}

            {criterion.criteria_state_type === 3 && (
                <div
                    className="survey-emoji-options"
                    role="radiogroup"
                    aria-label={groupLabel}
                >
                    {sortedStates.map((state) => {
                        const selected =
                            reply.state?.value === state.value ||
                            reply.state === state.value;
                        const glyph =
                            typeof state.emoji === "string" &&
                            state.emoji.trim() &&
                            !state.emoji.includes("<")
                                ? state.emoji.trim()
                                : "";
                        return (
                            <button
                                type="button"
                                key={state.id ?? state.value}
                                disabled={readOnly}
                                className={selected ? "selected" : ""}
                                onClick={() => selectState(state)}
                                aria-pressed={selected}
                                aria-label={state.name}
                            >
                                {glyph ? (
                                    <span
                                        className="survey-emoji-glyph"
                                        aria-hidden="true"
                                    >
                                        {glyph}
                                    </span>
                                ) : null}
                                <span>{state.name}</span>
                            </button>
                        );
                    })}
                </div>
            )}

            {criterion.criteria_state_type === 4 && (
                <div
                    className="survey-star-options"
                    role="group"
                    aria-label={groupLabel}
                >
                    <ReactStars
                        count={sortedStates.length}
                        value={
                            reply.state
                                ? sortedStates.findIndex(
                                      (state) =>
                                          state.value ===
                                          (reply.state?.value ?? reply.state)
                                  ) + 1
                                : 0
                        }
                        onChange={(position) => {
                            if (readOnly || position < 1) return;
                            selectState(sortedStates[position - 1]);
                        }}
                        size={32}
                        activeColor="#FEB730"
                        filledIcon={<StarIcon />}
                        emptyIcon={<EmptyStarIcon />}
                        edit={!readOnly}
                        isHalf={false}
                    />
                </div>
            )}

            {criterion.criteria_state_type === 5 && (
                <div className="survey-range-options">
                    <input
                        type="range"
                        min={0}
                        max={Math.max(sortedStates.length - 1, 0)}
                        step={1}
                        disabled={readOnly}
                        value={(() => {
                            const current = reply.state?.value ?? reply.state;
                            const position = sortedStates.findIndex(
                                (state) => state.value === current
                            );
                            return position >= 0 ? position : 0;
                        })()}
                        onChange={(event) => {
                            const position = Number(event.target.value);
                            selectState(sortedStates[position]);
                        }}
                        aria-label={groupLabel}
                    />
                    <div className="survey-range-caption" aria-live="polite">
                        {
                            sortedStates[
                                (() => {
                                    const current =
                                        reply.state?.value ?? reply.state;
                                    const position = sortedStates.findIndex(
                                        (state) => state.value === current
                                    );
                                    return position >= 0 ? position : 0;
                                })()
                            ]?.name
                        }
                    </div>
                </div>
            )}

            {criterion.add_description && (
                <textarea
                    className="survey-description"
                    rows={3}
                    disabled={readOnly}
                    name={`survey-description-${criterion.id}`}
                    autoComplete="off"
                    placeholder={t("survey.response.description.placeholder")}
                    aria-label={t("survey.response.description.placeholder")}
                    value={reply.description || ""}
                    onChange={(event) =>
                        onChange({
                            ...reply,
                            description: event.target.value,
                        })
                    }
                />
            )}

            {criterion.add_upload_file && (
                <div className="survey-file-upload">
                    <input
                        ref={fileInputRef}
                        type="file"
                        hidden
                        disabled={readOnly}
                        onChange={(event) => {
                            const file = event.target.files?.[0];
                            if (!file) return;
                            const reader = new FileReader();
                            reader.onload = () =>
                                onChange({
                                    ...reply,
                                    file: reader.result,
                                    fileName: file.name,
                                });
                            reader.readAsDataURL(file);
                        }}
                    />
                    <button
                        type="button"
                        disabled={readOnly}
                        onClick={() => fileInputRef.current?.click()}
                        aria-label={
                            reply.fileName ||
                            t("survey.response.file.attach")
                        }
                    >
                        <DocumentUpload size={20} aria-hidden="true" />
                        <span>
                            {reply.fileName ||
                                t("survey.response.file.attach")}
                        </span>
                    </button>
                </div>
            )}
        </fieldset>
    );
}
